import AddIcon from '@mui/icons-material/Add';
import { Draggable, Droppable, DragDropContext } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import CreateNewStepPlaceholder from './CreateNewStepPlaceholder/CreateNewStepPlaceholder';
import DraggableTactic from './DraggableTactic/DraggableTactic';
import { isOnlyOneTactic } from './TacticKanban.utils';
import TacticKanbanPagination from './TacticKanbanPagination';
import TacticStepControlPanel from './TacticStepControlPanel/TacticStepControlPanel';
import useTacticKanban from 'components/CepForm/Steps/TacticsStep/TacticKanban/TacticKanban.hook';
import {
  TacticKanbanButtonHeadStyle,
  OverlayStyle,
  ReorderingLoaderStyle,
  TacticCardListStyle,
  TacticKanbanLoaderStyle,
  StepCardWrapperStyle,
  TacticKanbanStyle,
  StepHeaderStyle,
} from 'components/CepForm/Steps/TacticsStep/TacticKanban/TacticKanban.styles';
import { Tactic } from 'generated/maya.types';
import Box from 'yoda-ui/components/Box';
import Button from 'yoda-ui/components/Button/Button';
import { ButtonType } from 'yoda-ui/components/Button/Button.types';
import Card from 'yoda-ui/components/Card/Card';
import CircularProgress from 'yoda-ui/components/CircularProgress';
import Loader from 'yoda-ui/components/Loader';
import { YodaFontWeight } from 'yoda-ui/yodaTheme';

const TacticKanban = () => {
  const {
    allGroupedTactics,
    groupedTactics,
    newElementButton,
    printViewButton,
    onDragEnd,
    onDragStart,
    loading,
    pagination,
    reorderSteps,
    setStepDependency,
    setStepMandatory,
    reorderingInProgress,
    idToStepMap,
    isTacticGroupingImprovementsEnabled,
    isDraggingTactic,
    moveTacticToStep,
    createStepFromTacticAtStart,
    createStepFromTactic,
    reindexAfterDeletion,
    isApprovedStartedCep,
    canReorderStepsInApprovedStartedCep,
    isCepApproved,
  } = useTacticKanban();
  const { t } = useTranslation();

  const isKanbanEmpty = Object.keys(groupedTactics).length === 0;
  const allTactics = Object.values(allGroupedTactics).flat();

  // Helper to handle reordering from the child component
  const handleStepReorder = async (sourceStep: string, destinationStep: string) => {
    const stepKeys = Object.keys(groupedTactics).sort((a, b) => +a - +b);
    const sourceIndex = stepKeys.indexOf(sourceStep);
    const destinationIndex = stepKeys.indexOf(destinationStep);

    if (sourceIndex !== -1 && destinationIndex !== -1) {
      await reorderSteps(sourceIndex, destinationIndex);
    }
  };

  return (
    <Box>
      <Box sx={ TacticKanbanButtonHeadStyle }>
        <Button
          buttonType={ ButtonType.outlined }
          onClick={ printViewButton.printViewClick }
        >
          { printViewButton.printViewLabel }
        </Button>
        {
          newElementButton?.newElementLabel ? (<Button
            buttonType={ ButtonType.primary }
            onClick={ newElementButton.newElementClick }
            disabled={ newElementButton.newElementDisabled }
          >
            { newElementButton.newElementLabel }
            <AddIcon sx={ { fontSize: '24px' } } />
          </Button>) : null
        }
      </Box>
      {
        loading && (
          <Box sx={ TacticKanbanLoaderStyle }>
            <Loader center/>
          </Box>
        )
      }
      <Box sx={ TacticKanbanStyle(isKanbanEmpty) }>
        {
          reorderingInProgress && (
            <Box sx={ OverlayStyle }>
              <CircularProgress sx={ ReorderingLoaderStyle }/>
            </Box>
          )
        }
        <DragDropContext onDragEnd={ onDragEnd } onDragStart={ onDragStart }>
          <Droppable droppableId="StepsList" type="Step">
            {
              (boardProvided) => (
                <Box
                  ref={ boardProvided.innerRef }
                  { ...boardProvided.droppableProps }
                >
                  {
                    isTacticGroupingImprovementsEnabled && !isKanbanEmpty && (
                      <CreateNewStepPlaceholder actionOrder="at-start" isDraggingTactic={ isDraggingTactic }/>
                    )
                  }
                  {
                    Object.keys(groupedTactics).map((actionOrder: string, columnIndex: number) => {
                      const tactics = groupedTactics[actionOrder];
                      const availableSteps = Object.keys(allGroupedTactics).filter((step) => step !== actionOrder);
                      const isGroupMandatory = tactics.every((tactic) => tactic.mandatory);
                      const isEditingDisabled = [
                        !isTacticGroupingImprovementsEnabled,
                        isTacticGroupingImprovementsEnabled && !availableSteps.length,
                        isApprovedStartedCep && !canReorderStepsInApprovedStartedCep,
                      ].some(Boolean);
                      const firstTactic = tactics[0];
                      const prevId = firstTactic?.previousTactic?._id;
                      const currentDependency = prevId ? idToStepMap.get(prevId) || null : null;

                      return (
                        tactics.length > 0 && (
                          <Draggable
                            draggableId={ actionOrder }
                            index={ columnIndex }
                            key={ actionOrder }
                            isDragDisabled={ isEditingDisabled }
                          >
                            {
                              (columnProvided) => (
                                <div
                                  ref={ columnProvided.innerRef }
                                  { ...columnProvided.draggableProps }
                                >
                                  <Card
                                    key={ actionOrder }
                                    variant="outlined"
                                    sx={ StepCardWrapperStyle(false) }
                                  >
                                    <Box
                                      { ...columnProvided.dragHandleProps }
                                      sx={ StepHeaderStyle(isEditingDisabled) }
                                    >
                                      <Box fontWeight={ YodaFontWeight.bold } >
                                        { `${t('maya_tactic_action_order')} ${actionOrder}` }
                                      </Box>
                                      {
                                        isTacticGroupingImprovementsEnabled
                                                  && <TacticStepControlPanel
                                                    actionOrder={ actionOrder }
                                                    availableSteps={ availableSteps }
                                                    allGroupedTactics={ allGroupedTactics }
                                                    isEditingDisabled={ isEditingDisabled }
                                                    currentDependency={ currentDependency }
                                                    isGroupMandatory={ isGroupMandatory }
                                                    onReorder={ (dest) => handleStepReorder(actionOrder, dest) }
                                                    onSetDependency={ (dependsOnStep) => setStepDependency(actionOrder, dependsOnStep) }
                                                    onSetMandatory={ (isMandatory) => setStepMandatory(actionOrder, isMandatory) }
                                                  />
                                      }
                                    </Box>

                                    <Droppable droppableId={ actionOrder } type="Tactic">
                                      {
                                        (listProvided, snapshot) => (
                                          <Box
                                            ref={ listProvided.innerRef }
                                            { ...listProvided.droppableProps }
                                            sx={
                                              {
                                                minHeight: '50px',
                                                backgroundColor: snapshot.isDraggingOver ? 'rgba(0, 135, 205, 0.05)' : 'transparent',
                                              }
                                            }
                                          >
                                            <Box sx={ TacticCardListStyle }>
                                              {
                                                tactics.map(
                                                  (tactic: Tactic, tacticIndex: number) => {
                                                    const tacticGroupId = tactic.parentTactic?._id;
                                                    const tacticsInSameGroupCount = allTactics
                                                      .filter((groupedTactic) => groupedTactic.parentTactic?._id === tacticGroupId)
                                                      .length;
                                                    const isGroupedTacticLocked = isCepApproved
                                                        && Boolean(tacticGroupId)
                                                        && tacticsInSameGroupCount <= 2;

                                                    return (
                                                      <DraggableTactic
                                                        key={ tactic._id }
                                                        tactic={ tactic }
                                                        index={ tacticIndex }
                                                        allGroupedTactics={ allGroupedTactics }
                                                        isOnlyOneTactic={ isOnlyOneTactic(groupedTactics) }
                                                        isDragDisabled={ isGroupedTacticLocked }
                                                        readOnly={ false }
                                                        onMoveToStep={ moveTacticToStep }
                                                        onCreateStepFromTacticAtStart={ createStepFromTacticAtStart }
                                                        onCreateStepFromTactic={ createStepFromTactic }
                                                        onTacticDeleted={ reindexAfterDeletion }
                                                        isTacticGroupingImprovementsEnabled={ isTacticGroupingImprovementsEnabled }
                                                        isDeleteDisabled={ isGroupedTacticLocked }
                                                        isMoveDisabled={ isGroupedTacticLocked }
                                                      />
                                                    );
                                                  },
                                                )
                                              }
                                              { listProvided.placeholder }
                                            </Box>
                                          </Box>
                                        )
                                      }
                                    </Droppable>
                                  </Card>
                                  {
                                    isTacticGroupingImprovementsEnabled
                                      && <CreateNewStepPlaceholder
                                        actionOrder={ actionOrder }
                                        isDraggingTactic={ isDraggingTactic }
                                      />
                                  }
                                </div>
                              )
                            }
                          </Draggable>
                        )
                      );
                    })
                  }
                  { boardProvided.placeholder }
                </Box>
              )
            }
          </Droppable>
        </DragDropContext>
      </Box>
      <TacticKanbanPagination
        count={ pagination.count }
        page={ pagination.page }
        rowsPerPage={ pagination.pageSize }
        onPageChange={ pagination.onPageChange }
        onRowsPerPageChange={ pagination.onPageSizeChange }
      />
    </Box>
  );
};

export default TacticKanban;
